/*
 * variant-availability.js  —  TICK-3385 / 3383 / 3384
 *
 * Keeps the product availability UI in sync with the selected variant when the
 * SwatchKing app (or the native selectors) change the variant. SwatchKing
 * dispatches the theme's `theme:variant:change` event, which we listen for.
 *
 * Source of truth is the server-rendered [data-variant-stock-map], because
 * Shopify does NOT serialize metafields into the client product JSON — so we
 * cannot recompute stock_state / lead-time on the client from inventory alone.
 *
 * Responsibilities:
 *   1. Reactively update [data-product-stock-status] (text + badge class), using
 *      the per-variant lead-time TEXT (3383). In-stock variants naturally drop
 *      the lead-time copy.
 *   2. Tag each SwatchKing swatch with its option-value availability
 *      (data-stock-state + a11y label) so customers see Ready to ship / Made to order
 *      per swatch.
 */
(function () {
  'use strict';

  // TICK-3951: 'in_stock_alt' = not in stock with the CURRENT selection, but
  // ready to ship in another combination ("Ready to ship in Walnut").
  var PRIORITY = { in_stock: 4, in_stock_alt: 3, mto: 2, sold_out: 1 };
  var CLASS_MOD = { in_stock: 'in-stock', in_stock_alt: 'in-stock-alt', mto: 'mto', sold_out: 'sold-out' };

  function readJSON(selector) {
    var el = document.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // TICK-3738 / TICK-3751: classify an option by its name so CSS can order the
  // groups (Size → Orientation → Upholstery → Base → Layout → everything else)
  // and strip redundant imagery from size dropdowns. First match wins —
  // upholstery is checked before base so "Base Upholstery" classifies as
  // upholstery. The keyword list is mirrored in
  // snippets/product-variant-options.liquid — keep them in sync.
  function classifyOption(name) {
    var n = String(name).trim().toLowerCase();
    if (n.indexOf('size') !== -1 || n.indexOf('height') !== -1 || n.indexOf('dimension') !== -1) return 'size';
    if (n.indexOf('orientation') !== -1 || n.indexOf('chaise') !== -1 || n.indexOf('facing') !== -1) return 'orientation';
    // TICK-3927: "Upholstery Style" (Baker Modular Sofas — Upholstered /
    // Slipcovered) must NOT classify as upholstery: that relabelled it
    // "Fabric & Color" on top of the real Upholstery option (David 8/19).
    // Checked before the upholstery keywords so the substring "upholstery"
    // never wins. A 'style' option keeps its own name, renders with the
    // Configuration button treatment and carries no RTS/MTO pills — the
    // style doesn't change availability, the fabric + orientation do.
    if (n.indexOf('style') !== -1) return 'style';
    // TICK-3942: a table TOP ("Top", "Top Material") is its own role, ordered
    // ABOVE base — David wants TOP over BASE with those exact labels on the
    // Tathra tables. Checked before the base keywords so "Top Material" no
    // longer files under base via 'material'. A 'top' option keeps its name.
    if (n.indexOf('top') !== -1) return 'top';
    if (n.indexOf('upholstery') !== -1 || n.indexOf('fabric') !== -1 || n.indexOf('leather') !== -1) return 'upholstery';
    if (n.indexOf('base') !== -1 || n.indexOf('wood') !== -1 || n.indexOf('finish') !== -1 || n.indexOf('material') !== -1) return 'base';
    if (n.indexOf('layout') !== -1 || n.indexOf('configuration') !== -1) return 'layout';
    return 'other';
  }

  function init() {
    var map = readJSON('[data-variant-stock-map]');
    var i18n = readJSON('[data-stock-i18n]');
    if (!map || !i18n) return;

    var section = document.querySelector('[data-section-type="product"]') || document;

    // TICK-4082: on the Ready to Ship / Made to Order picker templates the
    // picker (assets/rtsmto-picker.js) owns the options UI and Swatch King is
    // switched off. Skip ALL Swatch King work there — tagging, relabels,
    // config icons, swatch-link injection, "Ready to ship in …" pills and the
    // .swatches observer. The availability box, the Availability line-item
    // property, the quantity split and the hydrated-map merge keep running.
    var pickerMode = i18n.picker === 'rtsmto';

    // New-template mode (call w/ David 2026-07-02): the whole product set is
    // made to order, so per-option MTO/sold-out pills are redundant noise —
    // only in-stock values get a READY TO SHIP pill. The product-level pill +
    // availability box carry the made-to-order message instead.
    var hideMtoBadges = !!section.querySelector('[data-hide-mto-badges]');

    // TICK-3751: client-facing display names for Swatch King group labels
    // (keys are lowercased REAL admin option names), plus the swatch-request
    // link injected under the fabric option. Both served by
    // product-variant-stock-data.liquid.
    var displayNames = i18n.displayNames || {};
    // TICK-3778 RE#2: fallback keyed on the resolved role. classifyOption()
    // matches by substring, the name map needs a literal hit — so "Finish"
    // classified as 'base' but never relabelled. Exact name still wins.
    var displayNamesByRole = i18n.displayNamesByRole || {};
    var swatchRequest = i18n.swatchRequest || {};

    // ── Build an option-value → best-state index from the variant map ─────────
    // optionIndex[position][value] = best state across all variants with that value.
    var optionIndex = [];
    function buildOptionIndex() {
      optionIndex = [];
      Object.keys(map).forEach(function (id) {
        var entry = map[id];
        var opts = entry.o || [];
        opts.forEach(function (value, pos) {
          if (value == null) return;
          if (!optionIndex[pos]) optionIndex[pos] = {};
          var current = optionIndex[pos][value];
          if (!current || PRIORITY[entry.s] > PRIORITY[current]) {
            optionIndex[pos][value] = entry.s;
          }
        });
      });
    }
    buildOptionIndex();

    // ── Per-value pill state: selection-aware, in BOTH directions ────────────
    // History, because this has flipped twice:
    //   • David 7/7 (5e0f075): selection-aware — each value judged against
    //     combos keeping the other selected options fixed.
    //   • David 8/4 (TICK-3738 revision): aggregate — best state across ALL
    //     variants, because an in-stock fabric read MTO until its wood was
    //     picked (Noki "Westlake Java", Poise "Atlas Cigar").
    //   • David 8/21 call, TICK-3926 (MTG-15792): selection-aware again, and
    //     explicitly bi-directional — "the base finish should filter down to
    //     what's only available based on your variants … only the one that's
    //     actually ready to ship shows ready to ship"; picking a finish must
    //     re-judge the fabrics above it too, not just the options below.
    // So: a value is READY TO SHIP only if some variant with that value AND
    // the currently selected values of every other option is in stock. When
    // no selection is known yet (or the combo doesn't exist) fall back to the
    // aggregate so a pill never disappears. The availability box (updateBox)
    // still speaks for the finally selected combination.
    // ── Selection-aware pills with an "elsewhere" hint (TICK-3951) ───────────
    // Fourth and, David willing, final shape of this logic:
    //   7/7  selection-aware · 8/4 aggregate · 8/21 selection-aware both ways ·
    //   8/25 (3944) aggregate on load · 8/25 (3951) "but tell them Walnut has
    //   stock" + "the pill must never contradict the box" (Pivot Coffee Table).
    // Every value is judged against the CURRENT selection of the other
    // options (bi-directional, from page load). A value that is not in stock
    // with this selection but IS ready to ship in some other combination gets
    // the 'in_stock_alt' pill naming that combination ("Ready to ship in
    // Walnut") — so the Noki shopper on Light Oak sees Cigar has stock in
    // Walnut, Espresso Oak on the Noki fabric reads "Ready to ship in Sahara
    // Russet" on load, and a plain READY TO SHIP pill always agrees with the
    // availability box. The box (updateBox) speaks for the selected combo.
    var currentOptions = null; // ['Light Oak', 'Alabama | Cigar', ...] in option order
    var selectionIndex = null; // selectionIndex[pos][value] = best state given currentOptions
    var altIndex = null;       // altIndex[pos][value] = { names: [...] } — where else it's in stock

    function buildSelectionIndex() {
      selectionIndex = null;
      altIndex = null;
      if (!currentOptions) return;
      var idx = [];
      var alt = [];
      Object.keys(map).forEach(function (id) {
        var entry = map[id];
        var opts = entry.o || [];
        opts.forEach(function (value, pos) {
          if (value == null) return;
          // Which OTHER options differ from the current selection?
          var differs = [];
          for (var j = 0; j < opts.length; j++) {
            if (j === pos) continue;
            if (currentOptions[j] != null && opts[j] !== currentOptions[j]) differs.push(j);
          }
          if (!differs.length) {
            if (!idx[pos]) idx[pos] = {};
            var current = idx[pos][value];
            if (!current || PRIORITY[entry.s] > PRIORITY[current]) idx[pos][value] = entry.s;
            return;
          }
          if (entry.s !== 'in_stock') return;
          // In stock somewhere else: remember the differing value when exactly
          // one option differs (nameable), else flag as "other combinations".
          if (!alt[pos]) alt[pos] = {};
          var rec = alt[pos][value] || (alt[pos][value] = { names: [], generic: false });
          if (differs.length === 1) {
            var name = opts[differs[0]];
            if (rec.names.indexOf(name) === -1) rec.names.push(name);
          } else {
            rec.generic = true;
          }
        });
      });
      selectionIndex = idx;
      altIndex = alt;
    }

    function setCurrentOptions(opts) {
      if (!opts || !opts.length) return;
      var next = opts.map(function (v) { return v == null ? null : String(v); });
      if (currentOptions && next.join('\u0001') === currentOptions.join('\u0001')) return;
      currentOptions = next;
      buildSelectionIndex();
    }

    // Initial selection: the product form's variant id → its option values.
    // sections/product.liquid may have moved current_variant to the
    // Ready-to-Ship default, so the form input is the truth, not the URL.
    function readInitialSelection() {
      var idInput = section.querySelector('[data-product-form] [name="id"]')
        || section.querySelector('form[action*="/cart/add"] [name="id"]')
        || section.querySelector('[name="id"]');
      var entry = idInput && map[String(idInput.value)];
      if (entry && entry.o) setCurrentOptions(entry.o);
    }

    function valueState(pos, value) {
      var aggregate = optionIndex[pos] ? optionIndex[pos][value] : null;
      if (!selectionIndex) return aggregate;
      var selected = selectionIndex[pos] ? selectionIndex[pos][value] : null;
      if (selected === 'in_stock') return 'in_stock';
      if (altIndex && altIndex[pos] && altIndex[pos][value]) return 'in_stock_alt';
      return selected || aggregate;
    }

    // Names for the "Ready to ship in …" hint; '' when not applicable.
    function altNames(pos, value) {
      var rec = altIndex && altIndex[pos] && altIndex[pos][value];
      if (!rec) return '';
      if (!rec.names.length) return '*';
      // One name keeps the pill short in the closed dropdown row; "+N" says
      // there are more ("Ready to ship in Alabama | Pecan +1").
      return rec.names[0] + (rec.names.length > 1 ? ' +' + (rec.names.length - 1) : '');
    }

    // Pill wording per state; the alt pill names the other combination.
    function pillText(wording, state, pos, value) {
      if (state !== 'in_stock_alt') return (wording && wording[state]) || '';
      var names = altNames(pos, value);
      var tpl = i18n.alt || {};
      if (!names || names === '*') return tpl.other || (wording && wording.in_stock) || '';
      return String(tpl.with || '%values%').replace('%values%', names);
    }

    var optionNames = (i18n.optionNames || []).map(function (n) {
      return String(n).trim().toLowerCase();
    });

    // TICK-3738: finish colours sort light → dark (the "ladder"), not by stock
    // state. i18n.finishLadder lists the colours in display order; a value
    // list is only "laddered" when it has ≥2 values and EVERY value is a
    // ladder colour — any outside value disables the rule (all-or-nothing).
    var finishLadder = {};
    (i18n.finishLadder || []).forEach(function (v, i) {
      finishLadder[String(v).trim().toLowerCase()] = i;
    });
    function ladderIndexOf(value) {
      var idx = finishLadder[String(value).trim().toLowerCase()];
      return idx == null ? -1 : idx;
    }
    function isLaddered(values) {
      if (values.length < 2) return false;
      return values.every(function (v) { return ladderIndexOf(v) !== -1; });
    }

    // ── Availability box (TICK-3469) ─────────────────────────────────────────
    // Server renders the box for the initially selected variant
    // (product-stock-status.liquid); swap label + copy when the variant changes.
    // Per-variant lead-time text (entry.l) wins over the locale default.
    // TICK-3904: entry.l and i18n.detail arrive as HTML, prepared server-side
    // (richtext settings flattened to <br>-joined markup; plain metafield and
    // locale copy escaped + newline_to_br'd in Liquid) — that Liquid escaping
    // is the sanitization step that makes the innerHTML below safe.
    // TICK-3928 (8/21 call): the box is quantity-aware. entry.q is units on
    // hand (null when inventory isn't tracked). With 2 in stock, quantity 1–2
    // reads READY TO SHIP (TICK-3967: the "2 in stock" count is no longer
    // displayed — inventory counts are not public); quantity 3 flips to
    // MADE TO ORDER with "2 ready to ship now · 1 made to order" — David: "if
    // you added three of these, then on the fourth it would change to made to
    // order". Only ever the page-load count; a concurrent buyer is caught by
    // Shopify at checkout, and David accepted that (not enough traffic).
    var currentVariantId = null;
    var quantityInput = section.querySelector('[data-quantity-input]');
    function selectedQuantity() {
      var q = quantityInput ? parseInt(quantityInput.value, 10) : 1;
      return isNaN(q) || q < 1 ? 1 : q;
    }
    function countCopy(entry, qty) {
      var stock = entry.q;
      if (entry.s !== 'in_stock' || stock == null || stock <= 0 || !i18n.count) return { html: '', split: false };
      if (qty > stock) {
        return {
          html: String(i18n.count.split || '').replace('%rts%', stock).replace('%mto%', qty - stock),
          split: true
        };
      }
      // TICK-3967: no plain "N in stock" copy — inventory counts are not public.
      return { html: '', split: false };
    }

    function updateBox(variantId) {
      var entry = map[String(variantId)];
      if (!entry || !entry.s) return;
      currentVariantId = String(variantId);

      // Call w/ David 2026-07-02: everything is made to order unless it is in
      // stock, so an out-of-stock variant still shows the made-to-order message
      // ("the delivery time message should show up all the time if a product is
      // made to order or out of stock").
      var state = entry.s === 'sold_out' ? 'mto' : entry.s;
      var qty = selectedQuantity();
      var count = countCopy(entry, qty);
      // Past the on-hand count the order is (partly) made to order — the box
      // speaks for the whole line, so it takes the MTO state and copy.
      if (count.split) state = 'mto';
      // TICK-4082 Iteration 4 (Laleh: "tab says MADE TO ORDER, box says in
      // stock — which one is it?"): on the picker's Made to Order tab the box,
      // and with it the cart's Availability property, read Made to Order even
      // when the combination happens to be in stock. The pane offers the
      // Ready to Ship route instead. OPEN (Jody ↔ Laleh): whether such an
      // order ships from stock — if it does, revisit the property here.
      var picker = section.querySelector('[data-rtsmto-picker]');
      if (picker && picker.getAttribute('data-active-tab') === 'mto' && state === 'in_stock') {
        state = 'mto';
        count = { html: '', split: false };
        // The variant's own lead-time copy describes its in-stock delivery; use the MTO default.
        entry = { s: entry.s, o: entry.o, q: entry.q, l: '' };
      }

      // Product-level "Made to Order" pill (new template) mirrors the box state.
      section.querySelectorAll('[data-stock-pill]').forEach(function (pill) {
        pill.className = 'stock-status-pill badge badge--' + CLASS_MOD[state];
        pill.textContent = (i18n.label && i18n.label[state]) || '';
        var pillWrap = pill.closest('[data-product-stock-status]');
        if (pillWrap) pillWrap.setAttribute('data-product-stock-status', state);
      });

      var box = section.querySelector('[data-stock-detail-box]');
      if (!box) return;

      box.className = 'stock-status-box stock-status-box--' + CLASS_MOD[state];
      box.setAttribute('data-stock-state', state);

      var wrap = box.closest('[data-product-stock-status]');
      if (wrap) wrap.setAttribute('data-product-stock-status', state);

      var detail = entry.l || (i18n.detail && i18n.detail[state]) || '';

      var label = box.querySelector('[data-stock-detail-label]');
      // TICK-3778 #7: no trailing colon. Must match the Liquid in
      // product-stock-status.liquid — this runs on every variant change, so a
      // colon here would reappear the moment the shopper picks an option.
      if (label) label.textContent = (i18n.label && i18n.label[state]) || '';

      var text = box.querySelector('[data-stock-detail-text]');
      // TICK-3904: detail is server-prepared HTML (see note above updateBox) —
      // textContent here would show Laleh's <strong>/<br> tags literally.
      if (text) text.innerHTML = detail;

      // Call 2026-07-09: name the selected combination inside the message.
      var variantTitle = box.querySelector('[data-stock-variant-title]');
      if (variantTitle && entry.o) variantTitle.textContent = entry.o.filter(Boolean).join(' / ');

      // TICK-3928: count / split line. i18n.count.* is locale HTML (the split
      // wraps numbers in <strong>); the numbers are ours, not user input.
      var countEl = box.querySelector('[data-stock-count]');
      if (countEl) {
        countEl.innerHTML = count.html;
        if (count.html) countEl.removeAttribute('hidden'); else countEl.setAttribute('hidden', '');
      }

      writeAvailabilityProperty(entry, state, count, detail);
    }

    // TICK-3928: carry the availability into the cart line → checkout → order
    // as a line-item property ("Availability: Ready to Ship · Estimated
    // Delivery: 1–2 weeks (California) …"). David: "so you guys know later
    // on when you're looking through all of the orders". The cart page
    // renders its own live copy (cart-line-availability.liquid) and hides
    // this property; checkout and the order show it. Kept as a hidden input
    // in the product form so the theme's add-to-cart FormData picks it up
    // without touching theme.js. Written on every box update so it always
    // matches what the shopper is looking at when they click Add.
    var productForm = section.querySelector('[data-product-form]');
    function plainText(html) {
      var div = document.createElement('div');
      div.innerHTML = String(html || '').replace(/<br\s*\/?>/gi, ' · ');
      return div.textContent.replace(/\s+/g, ' ').replace(/(\s*·\s*)+/g, ' · ').replace(/^\s*·\s*|\s*·\s*$/g, '').trim();
    }
    function writeAvailabilityProperty(entry, state, count, detailHtml) {
      if (!productForm || !i18n.propertyLabel) return;
      var label = (i18n.label && i18n.label[state]) || '';
      // TICK-3946: no count in the property — it must match the string the
      // cart split writes (cart-line-availability.liquid) so a later add of
      // the same variant merges into the ready-to-ship line, and checkout
      // reads the same label per line either way.
      var parts = [label];
      var detailText = plainText(detailHtml);
      if (detailText) parts.push(detailText);
      var value = parts.filter(Boolean).join(' · ');
      var name = 'properties[' + i18n.propertyLabel + ']';
      var input = productForm.querySelector('input[name="' + name.replace(/"/g, '\\"') + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-availability-property', '');
        productForm.appendChild(input);
      }
      if (input.value !== value) input.value = value;
    }

    // ── SwatchKing per-swatch availability tagging ───────────────────────────
    // SwatchKing's markup varies by preset: the image presets expose the option
    // name as .swatch-option-name, the text-only dropdown presets don't emit that
    // element at all and label the group with .swatch-label-container. Try each,
    // most specific first, and drop any ": value" suffix a preset appends.
    // Returning '' is safe — all callers degrade rather than break.
    // TICK-3751: relabeling rewrites the visible label text, so the ORIGINAL
    // option name is preserved on the group as data-sk-name and read back first.
    function labelElOf(groupEl) {
      return groupEl.querySelector('.swatch-option-name')
          || groupEl.querySelector('.swatch-label-container')
          || groupEl.querySelector('.swatch-label');
    }

    // TICK-3751 (follow-up): a dropdown group renders TWO label nodes — the
    // VISIBLE one is .swatch-label (a <label> wrapping an aria-hidden checkbox +
    // the name text node), while .swatch-label-container is a 0-height hidden
    // wrapper. labelElOf() returns the container first, so the relabel landed on
    // the hidden node and the visible "Upholstery"/"Base" text never changed.
    // Relabel EVERY label node in the group instead; relabelGroup is per-element
    // idempotent (data-sk-relabeled) and only rewrites the matching text node, so
    // the checkbox's name="Upholstery" (backend option name) is left intact.
    function labelElsOf(groupEl) {
      return groupEl.querySelectorAll('.swatch-option-name, .swatch-label-container, .swatch-label');
    }

    function optionNameOf(groupEl) {
      var stored = groupEl.getAttribute && groupEl.getAttribute('data-sk-name');
      if (stored) return stored;
      var el = labelElOf(groupEl);
      if (!el) return '';
      return String(el.textContent).split(':')[0].trim();
    }

    // TICK-3751: rewrite a Swatch King group label to its client-facing display
    // name, preserving any ": value" suffix. Runs inside the MutationObserver
    // debounce, so a repeat pass MUST be a complete no-op (write only on change).
    // A rewritten element is stamped data-sk-relabeled and skipped on later
    // passes — once the name node no longer contains the original token, the
    // text-node walk could otherwise drift into a VALUE node that happens to
    // contain the original word (e.g. "2-Seater Layout").
    function relabelGroup(labelEl, originalName, display) {
      if (!labelEl) return;
      if (labelEl.hasAttribute('data-sk-relabeled')) return;
      if (labelEl.children && labelEl.children.length > 0) {
        // Label hosts element children — rewrite only the text node carrying the
        // name; if none matches, skip rather than clobber child elements.
        var walker = document.createTreeWalker(labelEl, NodeFilter.SHOW_TEXT, null);
        var node;
        while ((node = walker.nextNode())) {
          var textVal = node.nodeValue || '';
          if (textVal.indexOf(originalName) === -1) continue;
          // Already relabeled (e.g. "Base" → "Base Finish" still contains "Base").
          if (textVal.indexOf(display) !== -1) return;
          node.nodeValue = textVal.replace(originalName, display);
          labelEl.setAttribute('data-sk-relabeled', '');
          return;
        }
        return;
      }
      var text = String(labelEl.textContent);
      var sep = text.indexOf(':');
      var namePart = sep === -1 ? text : text.slice(0, sep);
      var suffix = sep === -1 ? '' : text.slice(sep);
      if (namePart.trim() === display) return; // idempotent no-op
      labelEl.textContent = display + suffix;
      labelEl.setAttribute('data-sk-relabeled', '');
    }

    function positionForGroup(groupEl, domIndex) {
      var name = optionNameOf(groupEl).toLowerCase();
      if (name) {
        var idx = optionNames.indexOf(name);
        if (idx !== -1) return idx;
      }
      return domIndex; // fall back to DOM order
    }

    function swatchValue(swatchEl) {
      var titleEl = swatchEl.querySelector('.swatch-button-title-text');
      if (titleEl) return titleEl.textContent.trim();
      // Fallback to the element's own text, excluding any label we injected so a
      // re-read after tagging can't pollute the option value.
      var raw = '';
      swatchEl.childNodes.forEach(function (node) {
        if (node.nodeType === 1 && node.classList.contains('sk-stock-label')) return;
        raw += node.textContent || '';
      });
      return raw.trim();
    }

    // TICK-3751 (follow-up): "Configuration" selector. Laleh wants the orientation
    // option rendered as the icon buttons in configuration.png — a sofa glyph plus
    // "Left Facing" / "Right Facing", replacing Swatch King's plain text buttons.
    // Display-only: the admin option name stays "Orientation" (heading relabelled
    // to "Configuration" via displayNames) and the values stay "Left"/"Right" (the
    // " Facing" suffix is a CSS ::after, so swatchValue() still reads the real value
    // for availability matching). The sofa art lives in the theme markup at
    // [data-config-icon-source] (product-variant-stock-data.liquid) so it can be
    // swapped without touching JS; we clone the matching glyph into each button.
    // Idempotent: re-running (observer / variant change) skips buttons already done.
    function configIconFor(direction) {
      // Query the document, not section — the art is identical everywhere and the
      // source markup may sit just outside [data-section-type="product"].
      var src = document.querySelector('[data-config-icon-source] [data-config-icon="' + direction + '"]');
      var svg = src && src.firstElementChild;
      return svg ? svg.cloneNode(true) : null;
    }

    function decorateConfigGroup(group) {
      group.querySelectorAll('.swatch-selector').forEach(function (sw) {
        var val = swatchValue(sw);
        if (!/left|right/i.test(val)) return; // only directional values get a glyph
        var isRight = /right/i.test(val);

        // TICK-3778 QA (Laleh): Left must read on the left, Right on the right.
        // Swatch King emits the buttons in ADMIN option-value order, and these
        // products list "Right Chaise" first — so the labels came out reversed
        // against their position. Ordering the flex child here rather than
        // resequencing the option values in admin keeps Swatch King's per-product
        // config, existing variant URLs and the RTS/MTO dropdowns untouched.
        // Runs before the already-decorated bail-out so a Swatch King re-render
        // that rebuilds the row still gets ordered. Write-on-change only: this
        // sits inside the MutationObserver debounce and must not self-trigger.
        var li = sw.closest('.swatch-view-item');
        if (li) {
          var wantedOrder = isRight ? '2' : '1';
          if (li.style.order !== wantedOrder) li.style.order = wantedOrder;
        }

        if (sw.querySelector('.config-icon-wrap')) return; // already decorated
        var titleEl = sw.querySelector('.swatch-button-title-text');
        if (!titleEl) return;
        var icon = configIconFor(isRight ? 'right' : 'left');
        if (!icon) return;
        var wrap = document.createElement('span');
        wrap.className = 'config-icon-wrap';
        wrap.appendChild(icon);
        var host = sw.querySelector('.swatch-title-and-icon-container') || titleEl.parentElement;
        host.insertBefore(wrap, host.firstChild);
        // "Left"/"Right" → "Left Facing"/"Right Facing" (skip values that already
        // carry a word, e.g. "Left Chaise", and never double up on "…Facing").
        if (/^(left|right)$/i.test(val.trim())) titleEl.setAttribute('data-config-suffix', ' Facing');
      });
    }

    function tagSwatches() {
      if (pickerMode) return; // TICK-4082
      var groups = section.querySelectorAll('.swatch-single');
      groups.forEach(function (group, domIndex) {
        // TICK-3738: stamp the option role so CSS can order the groups and
        // strip images from size dropdowns. Attribute writes don't trigger
        // the childList MutationObserver, so this can't loop.
        var roleName = optionNameOf(group);
        if (roleName) {
          // TICK-3751: keep the ORIGINAL admin option name on the group — the
          // relabel below rewrites the visible text, and later passes must
          // still resolve the real name (position lookup + display map key).
          if (!group.getAttribute('data-sk-name')) group.setAttribute('data-sk-name', roleName);
          var role = classifyOption(roleName);
          group.setAttribute('data-option-role', role);
          // TICK-3751: client-facing label ("Fabric & Color" etc.) — written in
          // title case; CSS handles the caps. Relabel every label node so the
          // dropdown presets' visible .swatch-label gets swapped too, not just the
          // hidden .swatch-label-container.
          // TICK-3778 RE#2: table-top options keep their own name ("leave TOP as
          // is"). "Top Material" classifies as 'base' on the keyword 'material',
          // which would label a table TOP as BASE FINISH. Exact-name hits still
          // win; only the role fallback is held back.
          var lowerName = roleName.toLowerCase();
          var display = displayNames[lowerName]
            || (lowerName.indexOf('top') === -1 ? displayNamesByRole[role] : null);
          if (display) {
            labelElsOf(group).forEach(function (labelEl) {
              relabelGroup(labelEl, roleName, display);
            });
          }
          // CSS `order` only applies to a direct flex child, and SwatchKing nests
          // each group in its own unclassed wrapper div inside .swatches — so the
          // rules never matched anything when they sat on .swatch-single. Walk up
          // to the element that IS the flex child (the first ancestor whose parent
          // holds more than one group, or .swatches itself), mirror the role onto
          // it, and mark its parent as the ordering container.
          var child = group;
          while (child.parentElement
                 && !child.parentElement.classList.contains('swatches')
                 && child.parentElement.children.length === 1) {
            child = child.parentElement;
          }
          if (child.parentElement) {
            child.setAttribute('data-option-role', role);
            child.parentElement.setAttribute('data-swatch-order-host', '');
          }

          // TICK-3751 (follow-up): dress the orientation option as the
          // "Configuration" icon selector (configuration.png). CSS scoped to
          // [data-option-role="orientation"] handles layout + the hidden pill.
          if (role === 'orientation') decorateConfigGroup(group);
        }

        var pos = positionForGroup(group, domIndex);
        var values = optionIndex[pos];
        if (!values) return;

        // TICK-3927: no availability pills on a 'style' option (Upholstery
        // Style). Strip any pill a stale render left behind and stop here —
        // the group still gets its role/order stamping above.
        if (group.getAttribute('data-option-role') === 'style') {
          group.querySelectorAll('.sk-stock-label').forEach(function (stale) { stale.remove(); });
          return;
        }

        // .swatch-selector is common to all SwatchKing swatch types — button,
        // dropdown item, AND image swatches (e.g. the wood/Base option). TICK-3407.
        group.querySelectorAll('.swatch-selector').forEach(function (sw) {
          var val = swatchValue(sw);
          if (!(val in values)) return;
          var state = valueState(pos, val);
          if (!state) return;
          var altKey = state === 'in_stock_alt' ? altNames(pos, val) : '';
          // Already tagged with this state AND (for the alt pill) the same names.
          if (sw.getAttribute('data-stock-state') === state && (sw.getAttribute('data-stock-alt') || '') === altKey) return;

          sw.setAttribute('data-stock-state', state);
          if (altKey) sw.setAttribute('data-stock-alt', altKey); else sw.removeAttribute('data-stock-alt');

          // New-template mode hides MTO/sold-out pills; the alt pill is an
          // in-stock signal, so it stays.
          if (hideMtoBadges && (state === 'mto' || state === 'sold_out')) {
            var stale = sw.querySelector('.sk-stock-label');
            if (stale) stale.remove();
            return;
          }

          var label = sw.querySelector('.sk-stock-label');
          if (!label) {
            label = document.createElement('span');
            sw.appendChild(label);
          }
          // Reuse the theme badge styling so swatch labels match the sibling badges.
          // TICK-3470: dropdown rows get the full wording (READY TO SHIP /
          // MADE-TO-ORDER per SOBU TEMPLATE); compact image swatches keep MTO.
          var wording = sw.closest('.swatch-drop-down-list-item') ? (i18n.dropdown || i18n.swatch) : i18n.swatch;
          label.className = 'sk-stock-label badge badge--' + CLASS_MOD[state];
          label.textContent = pillText(wording, state, pos, val);
        });
      });

      // TICK-3407: sort in-stock options to the top of each fabric/leather
      // dropdown (in_stock → mto → sold_out), preserving order within each group.
      // Only reorder when the order actually changes, to avoid mutation loops.
      section.querySelectorAll('.swatch-drop-down-list').forEach(function (list) {
        var items = Array.prototype.slice.call(list.querySelectorAll(':scope > .swatch-drop-down-list-item'));
        if (items.length < 2) return;

        // TICK-3738: finish-colour dropdowns sort by the light→dark ladder
        // instead of stock state. Read each row's value the same way the
        // tagger does (rows contain — or are — the .swatch-selector element).
        function itemValue(li) {
          var sel = li.matches('.swatch-selector') ? li : li.querySelector('.swatch-selector');
          return sel ? swatchValue(sel) : '';
        }
        var laddered = isLaddered(items.map(itemValue));

        var sorted = items.slice().sort(function (a, b) {
          if (laddered) return ladderIndexOf(itemValue(a)) - ladderIndexOf(itemValue(b));
          return (PRIORITY[b.getAttribute('data-stock-state')] || 0) - (PRIORITY[a.getAttribute('data-stock-state')] || 0);
        });
        var changed = sorted.some(function (li, i) { return li !== items[i]; });
        if (changed) sorted.forEach(function (li) { list.appendChild(li); });

        // TICK-3778 #3: the "MADE TO ORDER OPTIONS" group heading (TICK-3470) is
        // gone — Laleh asked for the internal headline out of the dropdowns.
        // The strip stays rather than just dropping the insert: SwatchKing
        // re-renders the list from its own cache, so a heading can still come
        // back in a stale render. Runs every pass, no-op once clean.
        var heading = list.querySelector(':scope > .sk-stock-group-heading');
        if (heading) heading.remove();
      });

      // TICK-3751: "Request Complimentary Swatches" link under the fabric option
      // in the Swatch King stack (CSS `order: 4` places it visually). Idempotent:
      // appending fires the observer once, so the debounced re-run must be a
      // complete no-op when the link is already present and unchanged.
      var skLinkPresent = false;
      section.querySelectorAll('[data-swatch-order-host]').forEach(function (host) {
        var link = host.querySelector(':scope > a.swatch-request-link[data-sk-injected]');
        // TICK-3778 #6: no upholstery gate. Laleh wants the link on every WS
        // template; requiring a fabric option on the open product meant the
        // dining tables never showed it.
        var wanted = !!(swatchRequest.text && swatchRequest.url);
        if (!wanted) {
          if (link) link.remove();
          return;
        }
        skLinkPresent = true;
        if (!link) {
          link = document.createElement('a');
          link.className = 'swatch-request-link';
          link.setAttribute('data-sk-injected', '');
          link.textContent = swatchRequest.text;
          link.href = swatchRequest.url;
          host.appendChild(link);
          return;
        }
        if (link.textContent !== swatchRequest.text) link.textContent = swatchRequest.text;
        if (link.getAttribute('href') !== swatchRequest.url) link.setAttribute('href', swatchRequest.url);
      });
      // When SwatchKing hosts the link, hide the Liquid-rendered native one so
      // the two stacks can't both show it. Inline style, not [hidden]: the
      // theme's `[hidden] { display: none }` ties with `.swatch-request-link
      // { display: inline-block }` on specificity and loses on source order.
      // No unhide when absent (the native stack is the sole renderer then).
      if (skLinkPresent) {
        section.querySelectorAll('.form__selectors > .swatch-request-link:not([data-sk-injected])').forEach(function (nativeLink) {
          if (nativeLink.style.display !== 'none') nativeLink.style.display = 'none';
        });
      }
    }

    // ── Theme-rendered dropdowns (select-popout) ──────────────────────────────
    // Liquid aggregates option-value states over at most 250 variants, so on
    // expanded-variant products the late option values render as SOLD OUT and
    // sort to the bottom. Re-derive every pill from the hydrated variant map
    // and re-apply the in_stock → mto → sold_out ordering. TICK-3595.
    function updatePopoutBadges(sortLists) {
      if (pickerMode) return; // TICK-4082
      section.querySelectorAll('[data-split-select-wrapper]').forEach(function (wrapper) {
        var pos = parseInt(wrapper.getAttribute('data-option-position'), 10) - 1;
        var values = optionIndex[pos];
        if (!values) return;
        // TICK-3927: 'style' options carry no pills (see tagSwatches).
        if (wrapper.getAttribute('data-option-role') === 'style') return;

        // TICK-3926: native swatch buttons carry the state as an attribute
        // (CSS corner-dot / border); Liquid stamped the aggregate, so re-stamp
        // against the current selection.
        wrapper.querySelectorAll('radio-swatch[data-stock-state]').forEach(function (sw) {
          var input = sw.querySelector('input[type="radio"]');
          if (!input || !(input.value in values)) return;
          var swState = valueState(pos, input.value);
          if (swState && sw.getAttribute('data-stock-state') !== swState) sw.setAttribute('data-stock-state', swState);
        });

        wrapper.querySelectorAll('[data-popout-option]').forEach(function (optionEl) {
          var value = optionEl.getAttribute('data-value');
          if (!(value in values)) return;
          var state = valueState(pos, value);
          if (!state) return;
          var badge = optionEl.querySelector('.badge');
          if (hideMtoBadges && (state === 'mto' || state === 'sold_out')) {
            if (badge) badge.remove();
            return;
          }
          if (!badge) {
            badge = document.createElement('span');
            optionEl.appendChild(badge);
          }
          badge.className = 'badge badge--' + CLASS_MOD[state];
          badge.textContent = pillText(i18n.swatch, state, pos, value);
        });

        if (sortLists) {
          wrapper.querySelectorAll('[data-popout-list]').forEach(function (list) {
            function stateOf(li) {
              var el = li.querySelector('[data-popout-option]');
              return (el && valueState(pos, el.getAttribute('data-value'))) || 'sold_out';
            }
            function valueOf(li) {
              var el = li.querySelector('[data-popout-option]');
              return (el && el.getAttribute('data-value')) || '';
            }
            var items = Array.prototype.slice.call(list.children).filter(function (li) { return li.querySelector('[data-popout-option]'); });
            if (items.length < 2) return;
            // TICK-3738: laddered finish colours keep light→dark order even
            // when data-prioritize-stock is set; other lists keep the stock
            // sort (and still only sort when the setting is on).
            var laddered = isLaddered(items.map(valueOf));
            if (!laddered && !wrapper.hasAttribute('data-prioritize-stock')) return;
            var sorted = items.slice().sort(function (a, b) {
              if (laddered) return ladderIndexOf(valueOf(a)) - ladderIndexOf(valueOf(b));
              return (PRIORITY[stateOf(b)] || 0) - (PRIORITY[stateOf(a)] || 0);
            });
            var changed = sorted.some(function (li, i) { return li !== items[i]; });
            if (changed) sorted.forEach(function (li) { list.appendChild(li); });
          });
        }
      });
    }

    // TICK-3595: theme.js pages in the variants past the 250-variant
    // serialization cap; fold their stock entries into the map and re-derive
    // all availability UI so late fabrics (e.g. L651) stop reading as sold out.
    document.addEventListener('theme:product:variants-hydrated', function (event) {
      var extra = event.detail && event.detail.map;
      if (!extra) return;
      var added = false;
      Object.keys(extra).forEach(function (id) {
        if (!map[id]) {
          map[id] = extra[id];
          added = true;
        }
      });
      if (!added) return;
      buildOptionIndex();
      buildSelectionIndex();
      tagSwatches();
      updatePopoutBadges(true);
    });

    // Re-tag after SwatchKing re-renders its UI. Not on the picker (TICK-4082).
    if (!pickerMode) {
      var swatchHost = section.querySelector('.swatches') || section;
      var observer = new MutationObserver(function () {
        window.clearTimeout(observer._t);
        observer._t = window.setTimeout(tagSwatches, 60);
      });
      observer.observe(swatchHost, { childList: true, subtree: true });
    }

    // ── Wire up ──────────────────────────────────────────────────────────────
    // Re-tag swatches when SwatchKing re-renders on variant change, and keep
    // the availability box in sync with the selected variant (TICK-3469).
    document.addEventListener('theme:variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      // TICK-3926: pills ARE selection-dependent — re-derive every group
      // against the new selection (both directions). Keep list order stable
      // mid-interaction (options jumping around while toggling reads as a
      // glitch), so no re-sort here.
      if (variant) {
        var entry = map[String(variant.id)];
        setCurrentOptions(variant.options || (entry && entry.o));
      }
      tagSwatches();
      updatePopoutBadges(false);
      if (variant && variant.id) updateBox(variant.id);
    });

    // TICK-3928: quantity changes re-evaluate the box for the same variant.
    if (quantityInput) {
      ['change', 'input'].forEach(function (type) {
        quantityInput.addEventListener(type, function () {
          if (currentVariantId) updateBox(currentVariantId);
        });
      });
    }

    readInitialSelection();
    tagSwatches();
    updatePopoutBadges(false);
    // Initial box pass: stamps the count for the default quantity and writes
    // the Availability property before the first Add to Cart.
    (function () {
      var idInput = productForm && productForm.querySelector('[name="id"]');
      if (idInput && map[String(idInput.value)]) updateBox(idInput.value);
      // The picker's tab changes the box without a variant change.
      document.addEventListener('rtsmto:tab', function (event) {
        var p = event.detail && event.detail.picker;
        if (p && !section.contains(p)) return;
        if (currentVariantId) updateBox(currentVariantId);
      });
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
